import { AxiosError, AxiosResponse } from 'axios';
import { getApi } from 'api';
import {
  IAbsenceReason,
  IAbsenceReasonRequest,
  IActiveAbsenceReason,
  ParsedAbsenceReasonError,
} from 'types/absence-reason/IAbsenceReason';

const BASE_URL = 'AbsenceReasons';

/** The shared axios instance has no timeout; without one the "request fails" ACs could never be reached. */
export const ABSENCE_REASON_REQUEST_TIMEOUT_MS = 30000;
const config = { timeout: ABSENCE_REASON_REQUEST_TIMEOUT_MS };

interface AbsenceReasonErrorBody {
  error?: unknown;
  details?: Record<string, unknown>;
}

/** Branches on ErrorResponse.error, never on `message` (same approach as parseFeatureAccessError). */
export const parseAbsenceReasonError = (error: unknown): ParsedAbsenceReasonError => {
  const response = (error as AxiosError<AbsenceReasonErrorBody>)?.response;
  const body = response?.data;
  return {
    status: response?.status ?? null,
    key: typeof body?.error === 'string' ? body.error : null,
    details: body?.details ?? {},
  };
};

export const getAbsenceReasonsRequest = async (): Promise<IAbsenceReason[]> => {
  const response: AxiosResponse<IAbsenceReason[]> = await getApi().get(BASE_URL, config);
  return response.data;
};

export const getAbsenceReasonRequest = async (id: number): Promise<IAbsenceReason> => {
  const response: AxiosResponse<IAbsenceReason> = await getApi().get(`${BASE_URL}/${id}`, config);
  return response.data;
};

/** Options for the no-show reason selector in service and contract confirmation. */
export const getActiveAbsenceReasonsRequest = async (): Promise<IActiveAbsenceReason[]> => {
  const response: AxiosResponse<IActiveAbsenceReason[]> = await getApi().get(`${BASE_URL}/Active`, config);
  return response.data;
};

export const createAbsenceReasonRequest = async (body: IAbsenceReasonRequest): Promise<IAbsenceReason> => {
  const response: AxiosResponse<IAbsenceReason> = await getApi().post(BASE_URL, body, config);
  return response.data;
};

export const updateAbsenceReasonRequest = async (
  id: number,
  body: IAbsenceReasonRequest,
  rowVersion: string,
): Promise<IAbsenceReason> => {
  const response: AxiosResponse<IAbsenceReason> = await getApi().put(
    `${BASE_URL}/${id}`,
    { ...body, rowVersion },
    config,
  );
  return response.data;
};

export const deactivateAbsenceReasonRequest = async (id: number, rowVersion: string): Promise<IAbsenceReason> => {
  const response: AxiosResponse<IAbsenceReason> = await getApi().post(
    `${BASE_URL}/${id}/Deactivate`,
    { rowVersion },
    config,
  );
  return response.data;
};
